import { FeatureFlag, FeatureFlagName } from "@/lib/feature-flag-api"
import type { RootState } from "@/lib/storage/redux-store"
import { baseApi } from "./base-api"

interface FeatureFlagsResponse {
  feature_flags: FeatureFlag[]
}

export const featureFlagApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFeatureFlags: builder.query<string[], void>({
      query: () => ({
        url: "/api/feature_flags",
        skipAuthHandling: true,
      }),
      transformResponse: (response: FeatureFlagsResponse) =>
        response.feature_flags.map((flag) => flag.name),
      providesTags: ["FeatureFlag"],
      // session-lifetime cache; refetch only on hard refresh (new store)
      keepUnusedDataFor: Infinity,
    }),
  }),
})

export const { useGetFeatureFlagsQuery } = featureFlagApi

/** The flag as thunks and selectors read it; false until the list has landed. */
export function selectFeatureFlagEnabled(
  state: RootState,
  name: FeatureFlagName
): boolean {
  const { data } = featureFlagApi.endpoints.getFeatureFlags.select()(state)
  return data?.includes(name) ?? false
}

// The one way to read a feature flag in a component. Subscribes to the enabled
// list and derives this flag's boolean via selectFromResult, so the component
// re-renders only when this flag's value changes. Present = on, absent = off.
// Safe to call from anywhere, authenticated or not:
// before a session exists the fetch resolves false rather than forcing re-auth.
export function useFeatureFlag(name: FeatureFlagName): boolean {
  const { enabled } = useGetFeatureFlagsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      enabled: data?.includes(name) ?? false,
    }),
  })
  return enabled
}
